/**
 * Shopify cart permalink format. A URL like
 *   https://store.com/cart/VARIANTID:QTY,VARIANTID:QTY
 * opens the merchant's cart page pre-populated with the items. The user then
 * hits "Check out" from there; Shopify handles everything (PCI, shipping,
 * payment). We never touch the merchant API.
 *
 * Works with any Shopify store — no tokens, no OAuth, no adapter-specific
 * code. If a merchant isn't on Shopify, each adapter (future) supplies its
 * own permalink builder.
 */
/** Same attribution the server stamps on its permalinks: the order carries a
 *  "source: mall-mart" note attribute and the UTMs feed marketing reports. */
const ATTRIBUTION: Record<string, string> = {
  "attributes[source]": "mall-mart",
  utm_source: "mall-mart",
  utm_medium: "vr-mall",
};

export function buildShopifyCartPermalink(
  storeOrigin: string,
  items: Array<{ variantId: string; quantity: number }>,
  buyerEmail?: string
): string {
  if (items.length === 0) {
    return `${storeOrigin}/cart`;
  }
  const parts: string = items
    .map((i) => {
      // Permalinks require numeric variant ids; strip gid:// prefixes.
      const numeric: string = i.variantId.replace(/\D/g, "");
      if (numeric === "") {
        throw new Error(`Invalid variant id for permalink: ${i.variantId}`);
      }
      return `${numeric}:${i.quantity}`;
    })
    .join(",");
  const params: URLSearchParams = new URLSearchParams(ATTRIBUTION);
  if (buyerEmail !== undefined && buyerEmail !== "") {
    // Pre-fills checkout and attaches the order to the signed-in shopper's
    // Shopify customer record.
    params.set("checkout[email]", buyerEmail);
  }
  return `${storeOrigin}/cart/${parts}?${params.toString()}`;
}
